import { PRICE_ADJUSTMENT_MODES, type PriceChangePreview } from "@station/contracts";
import { addDays, istanbulDateTimeToUtc, type LocalDate, toIstanbulDate } from "@station/core";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  cn,
  DateInput,
  FormField,
  Input,
  PageHeader,
  Select,
  SkeletonRows,
  Textarea,
  useToast,
} from "@station/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Check } from "lucide-react";
import { type ReactElement, useMemo, useState } from "react";
import { flushSync } from "react-dom";
import { useTranslation } from "react-i18next";
import { groupSelectionState, toggleGroupSelection } from "../../../features/price-group-values.js";
import { usePriceGroups } from "../../../features/price-groups.js";
import { api } from "../../../lib/api.js";
import { errorMessage } from "../../../lib/errors.js";
import { formatPriceChange, formatUnitPrice } from "../../../lib/format.js";
import { useDocumentTitle, useDraftGuard } from "../../../lib/hooks.js";
import { queryKeys } from "../../../lib/query.js";

export const Route = createFileRoute("/_app/fiyatlar/toplu-degisiklik")({ component: BulkPriceChangeWizard });

const STEPS = ["scope", "adjust", "schedule"] as const;
type Step = (typeof STEPS)[number];
const pairKey = (stationId: string, productId: string) => `${stationId}:${productId}`;

/**
 * Merkezi toplu fiyat değişikliği (Prompt 05): istasyon × ürün seç → yüzde/tutar/sabit ayarla → ÖNİZLE (hücreler elle
 * düzeltilebilir) → yürürlük zamanını planla. Hesap sunucuda `Decimal` ile yapılır; istemci float ile fiyat hesaplamaz.
 */
function BulkPriceChangeWizard() {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  useDocumentTitle(t("pricing.bulk.title"));

  const [step, setStep] = useState<Step>("scope");
  const [stationIds, setStationIds] = useState<Set<string>>(new Set());
  const [productIds, setProductIds] = useState<Set<string>>(new Set());
  const [mode, setMode] = useState<(typeof PRICE_ADJUSTMENT_MODES)[number]>("PERCENT");
  const [value, setValue] = useState("");
  const [roundTo, setRoundTo] = useState(2);
  const [newPrices, setNewPrices] = useState<Record<string, string>>({});
  const [skipped, setSkipped] = useState<PriceChangePreview["items"]>([]);
  const [date, setDate] = useState<LocalDate>(addDays(toIstanbulDate(new Date()), 1));
  const [time, setTime] = useState("00:00");
  const [source, setSource] = useState<"MANUAL" | "DISTRIBUTOR">("DISTRIBUTOR");
  const [note, setNote] = useState("");
  const [submitted, setSubmitted] = useState(false);
  useDraftGuard(Object.keys(newPrices).length > 0 && !submitted);

  const prices = useQuery({
    queryKey: queryKeys.prices({ bulk: true }),
    queryFn: async () => (await api.GET("/prices/current")).data!.items,
  });
  const stations = useMemo(
    () => [...new Map((prices.data ?? []).map((item) => [item.stationId, item])).values()],
    [prices.data],
  );
  const products = useMemo(
    () => [...new Map((prices.data ?? []).map((item) => [item.productId, item])).values()],
    [prices.data],
  );
  const current = useMemo(
    () =>
      new Map((prices.data ?? []).map((item) => [pairKey(item.stationId, item.productId), item.unitPrice])),
    [prices.data],
  );
  // Fiyat grupları (Prompt 16): grubu işaretlemek grubun istasyonlarını seçer (seçimin gerçeği istasyon kümesidir).
  const groups = usePriceGroups();
  const availableStationIds = useMemo(
    () => new Set(stations.map((station) => station.stationId)),
    [stations],
  );
  const selectableGroups = (groups.data ?? []).filter(
    (group) => group.isActive && group.stations.some((station) => availableStationIds.has(station.stationId)),
  );

  const preview = useMutation({
    mutationFn: async () =>
      (
        await api.POST("/price-change-requests/preview", {
          body: {
            stationIds: [...stationIds],
            productIds: [...productIds],
            adjustment: { mode, value: value.replace(",", "."), roundTo },
          },
        })
      ).data!,
    onSuccess: (data) => {
      // İstasyonda hiç satılmayan ürünler (fiyat çifti olmayan hücreler) talebe girmez.
      const relevant = data.items.filter(
        (item) => current.has(pairKey(item.stationId, item.productId)) || mode === "SET",
      );
      setNewPrices(
        Object.fromEntries(
          relevant
            .filter((item) => item.newUnitPrice)
            .map((item) => [
              pairKey(item.stationId, item.productId),
              item.newUnitPrice!.replace(/0{1,2}$/, ""),
            ]),
        ),
      );
      setSkipped(
        relevant.filter((item) => !item.newUnitPrice && current.has(pairKey(item.stationId, item.productId))),
      );
    },
    onError: (error) => toast({ tone: "danger", title: errorMessage(error) }),
  });

  const create = useMutation({
    mutationFn: async (submit: boolean) => {
      const items = Object.entries(newPrices)
        .filter(([, price]) => price.trim() !== "")
        .map(([key, price]) => ({
          stationId: key.split(":")[0]!,
          productId: key.split(":")[1]!,
          newUnitPrice: price.trim().replace(",", "."),
        }));
      // Tarih ve saat İSTANBUL saatidir; tarayıcının saat dilimi ne olursa olsun aynı an planlanır.
      const effectiveAt = istanbulDateTimeToUtc(date, time).toISOString();
      const created = (
        await api.POST("/price-change-requests", {
          body: { source, effectiveAt, note: note.trim() || null, items },
        })
      ).data!;
      if (submit)
        await api.POST("/price-change-requests/{id}/submit", { params: { path: { id: created.id } } });
      return created;
    },
    onSuccess: async (created) => {
      // Taslak koruması yönlendirmeden ÖNCE kalkmalıdır: durum eşzamanlı işlenmezse gezinme "kaydedilmemiş
      // değişiklik" uyarısına takılır.
      flushSync(() => setSubmitted(true));
      await queryClient.invalidateQueries({ queryKey: queryKeys.priceChangeRequests() });
      toast({ tone: "success", title: t("pricing.bulk.created", { requestNo: created.requestNo }) });
      await navigate({ to: "/fiyatlar", search: { tab: "requests", request: created.id } });
    },
    onError: (error) => toast({ tone: "danger", title: errorMessage(error) }),
  });

  if (prices.error) return <Alert tone="danger">{errorMessage(prices.error)}</Alert>;
  if (!prices.data) return <SkeletonRows rows={6} />;
  const toggle = (set: Set<string>, update: (next: Set<string>) => void, id: string) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    update(next);
  };
  const selectedStations = stations.filter((station) => stationIds.has(station.stationId));
  const selectedProducts = products.filter((product) => productIds.has(product.productId));
  const priceCount = Object.values(newPrices).filter((price) => price.trim() !== "").length;
  const stepIndex = STEPS.indexOf(step);

  return (
    <div className="mx-auto max-w-5xl">
      <PageHeader
        title={t("pricing.bulk.title")}
        description={t("pricing.bulk.description")}
        breadcrumb={[
          { label: t("menu.groups.catalog") },
          {
            label: t("menu.prices"),
            link: (children, className) => (
              <Link to="/fiyatlar" className={className}>
                {children}
              </Link>
            ),
          },
          { label: t("pricing.bulk.title") },
        ]}
      />
      <ol className="mb-6 flex flex-wrap gap-2" aria-label={t("pricing.bulk.steps")}>
        {STEPS.map((item, index) => (
          <li
            key={item}
            aria-current={item === step ? "step" : undefined}
            className={cn(
              "flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium",
              item === step && "border-primary bg-primary text-primary-foreground",
              index < stepIndex && "border-success/40 bg-success-subtle text-success",
            )}
          >
            <span
              className="flex size-5 items-center justify-center rounded-full bg-background/25 text-xs"
              aria-hidden="true"
            >
              {index < stepIndex ? <Check className="size-3.5" /> : index + 1}
            </span>
            {t(`pricing.bulk.step.${item}`)}
          </li>
        ))}
      </ol>

      {step === "scope" ? (
        <Card className="grid gap-6 p-5 sm:p-6 md:grid-cols-2">
          <div className="grid content-start gap-6">
            {selectableGroups.length > 0 ? (
              <fieldset className="grid content-start gap-2">
                <legend className="mb-2 text-sm font-semibold">{t("pricing.bulk.priceGroups")}</legend>
                <p className="text-xs text-muted-foreground">{t("pricing.bulk.priceGroupsHint")}</p>
                {selectableGroups.map((group) => {
                  const state = groupSelectionState(group, stationIds, availableStationIds);
                  return (
                    <label
                      key={group.id}
                      className="flex items-center gap-3 rounded-md border px-3 py-2 text-sm has-[:checked]:border-primary has-[:checked]:bg-primary/5"
                    >
                      <Checkbox
                        checked={state === "all"}
                        onChange={(event) =>
                          setStationIds(
                            toggleGroupSelection(
                              stationIds,
                              group,
                              availableStationIds,
                              event.target.checked,
                            ),
                          )
                        }
                      />
                      <span className="flex-1">
                        <span className="font-mono text-xs text-muted-foreground">{group.code}</span>{" "}
                        {group.name}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {state === "some"
                          ? t("pricing.bulk.groupPartial")
                          : t("pricing.bulk.groupStations", { count: group.stations.length })}
                      </span>
                    </label>
                  );
                })}
              </fieldset>
            ) : null}
            <Picker
              legend={t("menu.stations")}
              items={stations.map((station) => ({
                id: station.stationId,
                label: `${station.stationCode} · ${station.stationName}`,
              }))}
              selected={stationIds}
              onToggle={(id) => toggle(stationIds, setStationIds, id)}
              onAll={(all) => setStationIds(new Set(all ? stations.map((station) => station.stationId) : []))}
            />
          </div>
          <Picker
            legend={t("pricing.bulk.products")}
            items={products.map((product) => ({
              id: product.productId,
              label: product.productName,
              color: product.productColor,
            }))}
            selected={productIds}
            onToggle={(id) => toggle(productIds, setProductIds, id)}
            onAll={(all) => setProductIds(new Set(all ? products.map((product) => product.productId) : []))}
          />
        </Card>
      ) : null}

      {step === "adjust" ? (
        <Card className="grid gap-5 p-5 sm:p-6">
          <form
            className="flex flex-wrap items-end gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              preview.mutate();
            }}
          >
            <FormField label={t("pricing.bulk.mode")} className="w-56">
              {(control) => (
                <Select
                  {...control}
                  value={mode}
                  onChange={(event) => setMode(event.target.value as typeof mode)}
                >
                  {PRICE_ADJUSTMENT_MODES.map((item) => (
                    <option key={item} value={item}>
                      {t(`pricing.bulk.modes.${item}`)}
                    </option>
                  ))}
                </Select>
              )}
            </FormField>
            <FormField
              label={t(`pricing.bulk.valueLabel.${mode}`)}
              description={mode === "SET" ? undefined : t("pricing.bulk.valueHint")}
              className="w-44"
            >
              {(control) => (
                <Input
                  {...control}
                  value={value}
                  onChange={(event) => setValue(event.target.value)}
                  inputMode="decimal"
                  placeholder={mode === "PERCENT" ? "3,5" : "0,85"}
                  autoFocus
                />
              )}
            </FormField>
            <FormField label={t("pricing.bulk.roundTo")} className="w-40">
              {(control) => (
                <Select
                  {...control}
                  value={roundTo}
                  onChange={(event) => setRoundTo(Number(event.target.value))}
                >
                  {[2, 3, 4].map((digits) => (
                    <option key={digits} value={digits}>
                      {t("pricing.bulk.decimals", { count: digits })}
                    </option>
                  ))}
                </Select>
              )}
            </FormField>
            <Button
              type="submit"
              loading={preview.isPending}
              disabled={!/^-?\d+([.,]\d{1,4})?$/.test(value.trim())}
            >
              {t("pricing.bulk.preview")}
            </Button>
          </form>

          {skipped.length > 0 ? (
            <Alert tone="warning">{t("pricing.bulk.skipped", { count: skipped.length })}</Alert>
          ) : null}
          {priceCount > 0 ? (
            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full border-collapse text-sm">
                <caption className="sr-only">{t("pricing.bulk.previewCaption")}</caption>
                <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
                  <tr>
                    <th scope="col" className="px-3 py-2 text-left">
                      {t("menu.station")}
                    </th>
                    {selectedProducts.map((product) => (
                      <th key={product.productId} scope="col" className="min-w-36 px-3 py-2 text-right">
                        {product.productName}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {selectedStations.map((station) => (
                    <tr key={station.stationId} className="border-t">
                      <th scope="row" className="px-3 py-2 text-left font-medium">
                        <span className="font-mono text-xs text-muted-foreground">{station.stationCode}</span>{" "}
                        {station.stationName}
                      </th>
                      {selectedProducts.map((product) => {
                        const key = pairKey(station.stationId, product.productId);
                        const before = current.get(key) ?? null;
                        const price = newPrices[key];
                        return (
                          <td key={product.productId} className="px-2 py-1.5 text-right align-top">
                            {price === undefined ? (
                              <span className="text-muted-foreground">—</span>
                            ) : (
                              <div className="grid justify-items-end gap-0.5">
                                <Input
                                  value={price}
                                  onChange={(event) =>
                                    setNewPrices((all) => ({ ...all, [key]: event.target.value }))
                                  }
                                  inputMode="decimal"
                                  className="h-8 w-28 text-right tabular"
                                  aria-label={t("pricing.bulk.cellLabel", {
                                    station: station.stationName,
                                    product: product.productName,
                                  })}
                                />
                                <span className="text-xs text-muted-foreground tabular">
                                  {formatUnitPrice(before)}{" "}
                                  {/^\d+([.,]\d{1,4})?$/.test(price.trim())
                                    ? formatPriceChange(before, price.trim().replace(",", "."))
                                    : ""}
                                </span>
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">{t("pricing.bulk.previewHint")}</p>
          )}
        </Card>
      ) : null}

      {step === "schedule" ? (
        <Card className="grid gap-4 p-5 sm:p-6">
          <Alert tone="info">
            {t("pricing.bulk.summary", { stations: selectedStations.length, prices: priceCount })}
          </Alert>
          <div className="flex flex-wrap gap-4">
            <FormField
              label={t("pricing.bulk.effectiveDate")}
              description={t("pricing.bulk.effectiveHint")}
              required
              className="w-48"
            >
              {(control) => (
                <DateInput {...control} value={date} onValueChange={(next) => next && setDate(next)} />
              )}
            </FormField>
            <FormField label={t("pricing.bulk.effectiveTime")} required className="w-32">
              {(control) => (
                <Input
                  {...control}
                  type="time"
                  value={time}
                  onChange={(event) => setTime(event.target.value || "00:00")}
                />
              )}
            </FormField>
            <FormField label={t("pricing.source")} className="w-56">
              {(control) => (
                <Select
                  {...control}
                  value={source}
                  onChange={(event) => setSource(event.target.value as typeof source)}
                >
                  <option value="DISTRIBUTOR">{t("enums.priceSource.DISTRIBUTOR")}</option>
                  <option value="MANUAL">{t("enums.priceSource.MANUAL")}</option>
                </Select>
              )}
            </FormField>
          </div>
          <FormField label={t("pricing.requests.note")}>
            {(control) => (
              <Textarea
                {...control}
                value={note}
                onChange={(event) => setNote(event.target.value)}
                rows={2}
                maxLength={500}
              />
            )}
          </FormField>
        </Card>
      ) : null}

      <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
        <Button variant="outline" disabled={stepIndex === 0} onClick={() => setStep(STEPS[stepIndex - 1]!)}>
          {t("common.back")}
        </Button>
        {step === "schedule" ? (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => create.mutate(false)} loading={create.isPending}>
              {t("pricing.bulk.saveDraft")}
            </Button>
            <Button onClick={() => create.mutate(true)} loading={create.isPending}>
              {t("pricing.bulk.saveAndSubmit")}
            </Button>
          </div>
        ) : (
          <Button
            onClick={() => setStep(STEPS[stepIndex + 1]!)}
            disabled={step === "scope" ? stationIds.size === 0 || productIds.size === 0 : priceCount === 0}
          >
            {t("common.continue")}
          </Button>
        )}
      </div>
    </div>
  );
}

function Picker({
  legend,
  items,
  selected,
  onToggle,
  onAll,
}: {
  legend: string;
  items: Array<{ id: string; label: string; color?: string | null }>;
  selected: Set<string>;
  onToggle: (id: string) => void;
  onAll: (all: boolean) => void;
}): ReactElement {
  const { t } = useTranslation();
  return (
    <fieldset className="grid content-start gap-2">
      <legend className="mb-2 flex w-full items-center justify-between text-sm font-semibold">
        {legend}
        <Button variant="ghost" size="sm" onClick={() => onAll(selected.size !== items.length)}>
          {selected.size === items.length ? t("pricing.bulk.selectNone") : t("pricing.bulk.selectAll")}
        </Button>
      </legend>
      {items.map((item) => (
        <label
          key={item.id}
          className="flex items-center gap-3 rounded-md border px-3 py-2 text-sm has-[:checked]:border-primary has-[:checked]:bg-primary/5"
        >
          <Checkbox checked={selected.has(item.id)} onChange={() => onToggle(item.id)} />
          {item.color ? (
            <span
              className="size-2.5 rounded-full border"
              style={{ backgroundColor: item.color }}
              aria-hidden="true"
            />
          ) : null}
          {item.label}
        </label>
      ))}
    </fieldset>
  );
}
